<!-- ModalShell.vue -->
<!--
  Moldura reutilizável de um modal do Bootstrap.
  Encapsula `.modal` / `.modal-dialog` / `.modal-content` e o ciclo de vida
  via useBootstrapModal. Forneça o conteúdo pelos slots `header`, `body` e `footer`.

  @example
  <ModalShell ref="shell" id="meuModal" size="modal-xl">
    <template #header><ModalHeader title="Título" @close="..." /></template>
    <template #body> ... </template>
    <template #footer><ModalFooter @cancel="..." @confirm="..." /></template>
  </ModalShell>
-->
<template>
  <div
    class="modal fade"
    :id="id"
    :data-bs-backdrop="staticBackdrop ? 'static' : true"
    :data-bs-keyboard="keyboard"
    tabindex="-1"
    aria-hidden="true"
    ref="modalRef"
  >
    <div
      class="modal-dialog"
      :class="[
        size,
        {
          'modal-dialog-centered': centered,
          'modal-dialog-scrollable': scrollable,
          'modal-fullscreen-md-down': fullscreenMobile
        }
      ]"
    >
      <div class="modal-content">
        <div class="modal-header" v-if="$slots.header">
          <slot name="header" />
        </div>
        <div class="modal-body">
          <slot name="body" />
        </div>
        <div class="modal-footer" v-if="$slots.footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted } from 'vue'
import { onEventoDoModal, useBootstrapModal } from '@/composables/useBootstrapModal.js'

defineProps({
  id: {
    type: String,
    default: undefined
  },
  size: {
    type: String,
    default: 'modal-xl'
  },
  centered: {
    type: Boolean,
    default: false
  },
  // Prende a rolagem ao .modal-body em vez de deixar o modal crescer além da viewport.
  // Sem isto um modal alto empurra a barra de rolagem para a página inteira e o cabeçalho
  // sai de vista junto com o conteúdo — ligue em modal com lista.
  scrollable: {
    type: Boolean,
    default: false
  },
  staticBackdrop: {
    type: Boolean,
    default: true
  },
  keyboard: {
    type: Boolean,
    default: false
  },
  // Abaixo de md o modal ocupa a tela cheia (modal-fullscreen-md-down) — celular não tem
  // espaço para a moldura de .modal-dialog centralizada. Ligado por padrão; desligue só
  // para modais pequenos de confirmação onde a caixa centrada já é utilizável no celular.
  fullscreenMobile: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['hidden'])

const { modalRef, open, close } = useBootstrapModal()

let pararDeEscutar = () => {}

onMounted(() => {
  pararDeEscutar = onEventoDoModal(modalRef.value, 'hidden.bs.modal', () => emit('hidden'))
})
onBeforeUnmount(() => pararDeEscutar())

defineExpose({ open, close })
</script>
